import { debounce, DebouncedFunction } from 'es-toolkit';
import { useLocation } from 'preact-iso';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { VirtualizerHandle } from 'virtua';
import { addSessionPodcastId } from '../global-state/podcasts';
import { getUpdatedIsInApp, useSessionStore } from '../global-state/session';
import debugLog from './debug';
import { isPodcastIdentifier } from './helpers';
import addScrollListener from './scroll-listener';

let exposedRoute: (to: string, replace?: boolean) => void;

// Silly that we have to do this to expose route() globally for use in non-component code
export function GlobalRouteExposer() {
  const { route } = useLocation();
  exposedRoute = route;
  return null;
}

export function route(to: string, replace = false) {
  return exposedRoute(to, replace);
}

const log = (...args: any[]) =>
  debugLog({ logName: 'router', logColor: 'deeppink' }, ...args);

export function useHandleCurrentRoute() {
  const { path: currentRoute } = useLocation();

  const isInApp = getUpdatedIsInApp(currentRoute);

  const routes = useRef<string[]>([]);
  const scrollPositionByRoute = useRef<Record<string, number>>({});
  const saveScrollPositionRef = useRef<DebouncedFunction<() => void>>(null);

  useLayoutEffect(() => {
    // Keep track of route history and whether we're in the app or the landing pages
    routes.current.push(currentRoute);
    useSessionStore.setState({
      previousRoute: routes.current[routes.current.length - 2] || '',
      isInApp,
    });
    // Keep track of what podcasts have been visited in this session, so that we can save
    // their episodes in LocalStorage
    if (currentRoute.startsWith('/podcast')) {
      const podcastId = currentRoute.split('/')[2];
      if (isPodcastIdentifier(podcastId)) addSessionPodcastId(podcastId);
    }
    // Track, restore & reset scroll position
    const scrollContainer = window.pageScrollContainer;
    if (saveScrollPositionRef.current) {
      // DOM has now changed, don't save scroll position any longer for prior route
      saveScrollPositionRef.current.cancel();

      // Restore or reset scroll position for newly navigated-to route
      // The if-statement above ensures this isn't the very first route
      const scrollPosition = scrollPositionByRoute.current[currentRoute];

      if ('virtualizerHandle' in window && window.virtualizerHandle) {
        // If virtualized list is in use (e.g. on podcasts page), let it manage it
        (window.virtualizerHandle as VirtualizerHandle).scrollTo(scrollPosition);
      } else {
        setScrollPosition(scrollContainer, scrollPosition);
      }
    }
    // Save scroll position for current route
    saveScrollPositionRef.current = debounce(() => {
      scrollPositionByRoute.current[currentRoute] = scrollContainer.scrollTop;
    }, 100);
    return addScrollListener(scrollContainer, saveScrollPositionRef.current);
  }, [currentRoute]);

  return { isInApp, currentRoute };
}

function setScrollPosition(
  container: HTMLElement,
  amount: number | undefined,
  attemptCount = 1
) {
  const maxAttempts = 3;
  let action = 'restore scroll position';
  if (amount === undefined) {
    amount = 0;
    action = 'reset scroll position';
  }
  container.scrollTo({ top: amount, left: 0, behavior: 'instant' });
  const scrollTopAfterUpdate = container.scrollTop;
  if (Math.abs(scrollTopAfterUpdate - amount) > 10) {
    if (attemptCount >= maxAttempts) {
      return log(`Failed to ${action}`);
    }
    requestAnimationFrame(() => setScrollPosition(container, amount, ++attemptCount));
  } else {
    log(action, attemptCount, scrollTopAfterUpdate, amount);
  }
}
